/**
 * Overflow menu for commands whose layout slot is `overflow`. Spec §5.1.
 *
 * Duplicated from apps/web per spec §6.2. Keep in sync with the customer copy.
 *
 * One `DropdownMenuItem` per command — icon + translated label + destructive
 * styling when applicable. Items are disabled while any command is loading;
 * the actively dispatching item shows a spinner.
 */
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from '@staff-app/ui';
import type { MdmCommandDefinition, MdmCommandId } from '@staff-app/shared-types';
import { getIcon } from './MdmActionIcons';
import { getLayoutEntry, MDM_ACTION_LAYOUT, type MdmActionLayoutEntry } from './MdmActionLayout';

interface Props {
  items: MdmCommandDefinition[];
  loading: MdmCommandId | null;
  onSelect: (cmd: MdmCommandDefinition) => void;
  layout?: Record<MdmCommandId, MdmActionLayoutEntry>;
  /** Per-command reason to render the item disabled (shown as its tooltip/title). */
  disabledReasons?: Partial<Record<MdmCommandId, string | null | undefined>>;
}

export function MdmActionDropdown({
  items,
  loading,
  onSelect,
  layout = MDM_ACTION_LAYOUT,
  disabledReasons,
}: Props) {
  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={'More commands'}
          className="shrink-0 cursor-pointer gap-1.5"
          data-testid="mdm-action-overflow-trigger"
        >
          <span>{'More'}</span>
          <ChevronDown className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="mdm-action-overflow-menu">
        {items.map((cmd) => {
          const Icon = getIcon(cmd.id);
          const isLoading = loading === cmd.id;
          // Red tint is opt-in per-command via the layout. `cmd.destructive`
          // (shared catalog flag) drives confirm-dialog semantics only — UI
          // prominence is a layout decision so power-user actions can render
          // un-tinted while still being gated by a confirm dialog.
          const destructive = getLayoutEntry(cmd.id, layout).variant === 'destructive';
          const disabledReason = disabledReasons?.[cmd.id] || undefined;
          return (
            <DropdownMenuItem
              key={cmd.id}
              onSelect={() => onSelect(cmd)}
              disabled={loading !== null || !!disabledReason}
              title={disabledReason}
              className={cn(
                'cursor-pointer gap-2',
                destructive && 'text-destructive focus:text-destructive',
              )}
              data-testid={`mdm-action-overflow-${cmd.id}`}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Icon className="h-4 w-4" aria-hidden />
              )}
              <span>{cmd.i18nKey}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
